// Held production-batch actions and hand-overs clear on the station once a manager resolves
// the hold in the Admin (review CR8B-4; as deliveries do, CR6-102). The resolution comes down
// on the `station_holds` stream (`command_hold.status` leaves `open`): an applied hold makes
// the row `booked` (the office has it), a discarded one `voided` (a manager decided the office
// won't record it), with the manager's note kept in the row's answer. A resolved row no longer
// shows "needs a manager", and like any answered row it goes once its day is over (a
// hand-over's customer name included, CR7-9).

import type { LocalTx } from './engine.js';

/** The local tables whose held rows clear this way. */
export type HeldActionTable = 'batch_action' | 'handover_action';

/** A manager's resolution, kept in the row's answer. */
export interface HeldResolution {
  status: 'booked' | 'voided';
  note: string | null;
  resolved_at: string | null;
}

/** Held rows of `table` whose hold a manager has resolved. */
export function resolvedHeldSql(table: HeldActionTable): string {
  return `SELECT a.id, a.answer, h.status AS hold_status, h.resolution_note, h.resolved_at
  FROM ${table} a
  JOIN command_hold h ON h.id = json_extract(a.answer, '$.hold_id')
  WHERE a.status = 'held' AND h.status IS NOT NULL AND h.status != 'open'`;
}

export const RESOLVED_HELD_BATCH_ACTIONS_SQL = resolvedHeldSql('batch_action');
export const RESOLVED_HELD_HANDOVERS_SQL = resolvedHeldSql('handover_action');

/** Writes managers' resolutions onto held rows of `table`. Returns how many changed. */
export async function resolveHeldActions(tx: LocalTx, table: HeldActionTable): Promise<number> {
  const rows = await tx.getAll<{
    id: string;
    answer: string | null;
    hold_status: string;
    resolution_note: string | null;
    resolved_at: string | null;
  }>(resolvedHeldSql(table));
  for (const r of rows) {
    const status = r.hold_status === 'applied' ? 'booked' : 'voided';
    let answer: Record<string, unknown> = { flags: [] };
    try {
      if (r.answer) answer = JSON.parse(r.answer) as Record<string, unknown>;
    } catch {
      // A damaged answer keeps only the resolution.
    }
    const resolution: HeldResolution = {
      status,
      note: r.resolution_note,
      resolved_at: r.resolved_at,
    };
    await tx.execute(`UPDATE ${table} SET status = ?, answer = ? WHERE id = ?`, [
      status,
      JSON.stringify({ ...answer, resolution }),
      r.id,
    ]);
  }
  return rows.length;
}

/**
 * Deletes resolved (booked or voided) batch actions of days before `today`: the queue shows
 * today's work and anything still open, so they have nothing more to say. Returns how many.
 */
export async function pruneResolvedBatchActions(tx: LocalTx, today: string): Promise<number> {
  const old = await tx.getAll<{ id: string }>(
    `SELECT id FROM batch_action WHERE day < ? AND status IN ('booked', 'voided')
       AND batch_id NOT IN (SELECT id FROM production_batch WHERE status IN ('planned', 'started'))`,
    [today],
  );
  for (const r of old) await tx.execute('DELETE FROM batch_action WHERE id = ?', [r.id]);
  return old.length;
}
